import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WatchButton from "@/components/WatchButton";
import EmptyState from "@/components/EmptyState";
import KennelVerificationDrawer from "@/components/admin/KennelVerificationDrawer";
import VerificationProgressBadge from "@/components/VerificationProgressBadge";
import AddBreederDialog from "@/components/AddBreederDialog";
import { UsersFourIcon as UsersFour, PlusIcon as Plus, ShieldCheckIcon as ShieldCheck, StarIcon as Star, HeartIcon as Heart, StorefrontIcon as Storefront, MapPinIcon as MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { WaxSeal } from "@/components/luxury";
import { thumbUrl } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";

export default function Breeders() {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [breeders, setBreeders] = useState([]);
  const [open, setOpen] = useState(false);
  const [verifyDrawer, setVerifyDrawer] = useState({ open: false, id: null, name: null });
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    breed: "",
    location: "",
    has_dogs: false,
    has_studs: false,
  });

  const load = () => {
    const params = new URLSearchParams();
    if (filters.breed.trim())    params.set("breed", filters.breed.trim());
    if (filters.location.trim()) params.set("location", filters.location.trim());
    if (filters.has_dogs)        params.set("has_dogs", "true");
    if (filters.has_studs)       params.set("has_studs", "true");
    const q = params.toString();
    return api.get(`/breeders${q ? `?${q}` : ""}`).then((r) => setBreeders(r.data));
  };

  useEffect(() => {
    load();
  }, []);

  // Deep-link from `/admin/approvals` → open the verification drawer
  // automatically when `?verify=<breeder_id>` is in the URL. Once we
  // load the breeder list, hydrate the drawer header with the kennel
  // name and strip the query param so a refresh doesn't reopen it.
  useEffect(() => {
    const verifyId = searchParams.get("verify");
    if (!verifyId || !isAdmin || breeders.length === 0) return;
    if (verifyDrawer.open && verifyDrawer.id === verifyId) return;
    const target = breeders.find((b) => b.id === verifyId);
    setVerifyDrawer({
      open: true,
      id: verifyId,
      name: target?.kennel_name || target?.name || "Kennel",
    });
    const next = new URLSearchParams(searchParams);
    next.delete("verify");
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breeders, searchParams, isAdmin]);

  // Deep-link from `/dashboard` (CreateKennelNudge) and `/me/kennel`
  // (when the user has no kennel yet): `?create=1` auto-opens the
  // Add-Breeder dialog. We strip the query param immediately so a
  // refresh doesn't reopen the dialog.
  useEffect(() => {
    if (searchParams.get("create") !== "1") return;
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onCreated = (data) => {
    toast.success("Kennel created");
    setOpen(false);
    // If this is the calling user's own kennel (matched via email or
    // explicit user_id), jump them straight to the detail page so they
    // can claim a slug, upload a photo, and explore the benefits.
    // Otherwise (e.g. a buyer added a kennel for pedigree purposes),
    // just refresh the list.
    if (data?.user_id && user?.id && data.user_id === user.id) {
      navigate(`/breeders/${data.id}`);
    } else {
      load();
    }
  };

  // Admin "verify" button now opens the 7-step verification drawer
  // instead of directly flipping the seal — the seal can only be
  // conferred once every check passes (or via an audited override).
  const openVerifyDrawer = (b) => {
    setVerifyDrawer({ open: true, id: b.id, name: b.kennel_name || b.name });
  };
  const onVerifiedChanged = (next) => {
    if (verifyDrawer.id) {
      setBreeders((prev) => prev.map((x) => (x.id === verifyDrawer.id ? { ...x, verified: next } : x)));
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Breeders</p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">Kennels &amp; breeders</h1>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
            Working kennels and serious owners across the registry — purebreds, rare bloodlines, and exotic breeds. Search by breed, location, or stud availability.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11"
          data-testid="add-breeder-btn"
        >
          <Plus size={16} className="mr-1" /> Add breeder
        </Button>
        <AddBreederDialog
          open={open}
          onClose={() => setOpen(false)}
          onCreated={onCreated}
          existingBreeders={breeders}
        />
      </div>

      {/* Filter strip */}
      <div className="mb-6 rounded-xl border border-[#EAEAEA] bg-white p-4" data-testid="breeders-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-1 block">Breed</Label>
            <Input
              placeholder="Cane Corso, Boxer…"
              value={filters.breed}
              onChange={(e) => setFilters((f) => ({ ...f, breed: e.target.value }))}
              data-testid="breeders-filter-breed"
            />
          </div>
          <div className="md:col-span-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6B6B6B] mb-1 block">Location</Label>
            <Input
              placeholder="State or city"
              value={filters.location}
              onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}
              data-testid="breeders-filter-location"
            />
          </div>
          <div className="md:col-span-2 flex items-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, has_dogs: !f.has_dogs }))}
              data-testid="breeders-filter-has-dogs"
              className={`inline-flex items-center gap-1.5 rounded-full text-[12.5px] font-semibold px-4 h-11 transition-colors border ${
                filters.has_dogs ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#E5E5E5] hover:border-ink"
              }`}
            >
              <Storefront size={14} weight={filters.has_dogs ? "fill" : "duotone"} />
              Available dogs
            </button>
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, has_studs: !f.has_studs }))}
              data-testid="breeders-filter-has-studs"
              className={`inline-flex items-center gap-1.5 rounded-full text-[12.5px] font-semibold px-4 h-11 transition-colors border ${
                filters.has_studs ? "bg-ink text-white border-ink" : "bg-white text-ink border-[#E5E5E5] hover:border-ink"
              }`}
            >
              <Heart size={14} weight={filters.has_studs ? "fill" : "duotone"} />
              Available studs
            </button>
            {(filters.breed || filters.location || filters.has_dogs || filters.has_studs) && (
              <button
                type="button"
                onClick={() => setFilters({ breed: "", location: "", has_dogs: false, has_studs: false })}
                data-testid="breeders-filter-clear"
                className="text-[12px] text-[#6B6B6B] underline hover:text-ink"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {breeders.length === 0 ? (
        <EmptyState
          testId="breeders-empty"
          icon={<UsersFour size={32} weight="duotone" />}
          title="No breeders yet"
          description="Add a breeder profile so the dogs you register can be tied back to their kennel of origin. You can also browse public kennels."
          ctaLabel="Add a breeder"
          ctaOnClick={() => setOpen(true)}
          secondaryLabel="Public kennels"
          secondaryTo="/kennels"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {breeders.map((b) => {
            const isVerified = b.verified || b.is_verified || b.elite_verified || b.seller_plan === "verified";
            const isElite = !!b.elite_verified;
            const isFeatured = !!b.featured;
            const isUnclaimed = !!b.unclaimed;
            return (
            <div
              key={b.id}
              className={`relative border ${isElite ? "bg-white border-crimson/60 shadow-gold-glow" : isFeatured ? "bg-white border-gold/70 shadow-gold-glow" : isUnclaimed ? "bg-[#FAFAF8] border-dashed border-[#DDD8CC] opacity-80 hover:opacity-100" : "bg-white border-[#EAEAEA]"} hover:border-gold/55 active:scale-[0.99] rounded-xl p-6 shadow-luxury-light hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-500`}
              data-testid={`breeder-card-${b.id}`}
            >
              {isElite ? (
                <span
                  className="absolute -top-2 left-4 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#7A1A1A] via-[#C1272D] to-[#7A1A1A] text-white rounded-full px-2.5 py-0.5 shadow-[0_2px_6px_rgba(193,39,45,0.35)]"
                  data-testid={`breeder-elite-${b.id}`}
                  title="Elite Verified Breeder — prestige tier"
                >
                  <ShieldCheck size={9} weight="fill" /> Elite Verified
                </span>
              ) : isFeatured ? (
                <span
                  className="absolute -top-2 left-4 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider bg-gold-gradient text-ink rounded-full px-2 py-0.5"
                  data-testid={`breeder-featured-${b.id}`}
                >
                  <Star size={9} weight="fill" /> Featured kennel
                </span>
              ) : isUnclaimed ? (
                <span
                  className="absolute -top-2 left-4 inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider bg-[#8A8477] text-white rounded-full px-2.5 py-0.5"
                  data-testid={`breeder-unclaimed-${b.id}`}
                  title="No linked member account — added by a member for pedigree records"
                >
                  Unclaimed
                </span>
              ) : null}
              <div className="absolute top-4 right-4 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => openVerifyDrawer(b)}
                    title={isVerified ? "Manage verification (currently verified)" : "Open 7-step verification"}
                    aria-pressed={isVerified}
                    data-testid={`breeder-verify-toggle-${b.id}`}
                    className={`inline-flex items-center justify-center h-8 w-8 rounded-full border transition-all ${
                      isVerified
                        ? "bg-gold-gradient text-ink border-gold/60 shadow-wax-seal"
                        : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-gold/55 hover:text-gold"
                    }`}
                  >
                    <ShieldCheck size={14} weight={isVerified ? "fill" : "duotone"} />
                  </button>
                )}
                <WatchButton targetType="breeder" targetId={b.id} />
              </div>
              <Link to={`/breeders/${b.id}`} className="block">
                <div className={`flex items-center gap-3 mb-3 ${isAdmin ? "pr-16" : "pr-8"}`}>
                  <div className="relative flex-shrink-0">
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-gradient-to-br from-[#FBF7EC] to-[#F3E9C7] ring-1 ring-[#C9A227]/40 flex items-center justify-center shadow-[0_2px_6px_rgba(193,162,39,0.10)]">
                      {b.photo_url || b.logo_url ? (
                        <img src={thumbUrl(b.photo_url || b.logo_url)} alt={b.kennel_name || b.name} className="w-full h-full object-cover" data-testid={`breeder-logo-${b.id}`} />
                      ) : (
                        <UsersFour size={22} weight="duotone" className="text-[#8A6A00]" />
                      )}
                    </div>
                    {isVerified && <WaxSeal size={20} title="Verified kennel" className="absolute -bottom-0.5 -right-1" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-spectral text-xl tracking-tight leading-snug">{b.kennel_name || b.name}</h3>
                    {b.kennel_name && <p className="text-xs text-[#6B6B6B] truncate">{b.name}</p>}
                  </div>
                </div>
                <div className="gold-hairline mb-3" />
                {b.location && (
                  <div className="text-sm text-[#6B6B6B] flex items-center gap-1">
                    <MapPin size={11} weight="duotone" /> {b.location}
                  </div>
                )}
                {b.contact && <div className="text-xs text-[#6B6B6B] mt-1 font-tabular">{b.contact}</div>}
                {/* Live-inventory badges — buyer-facing signals that this
                    kennel actually has dogs available right now. */}
                {(b.has_dogs_for_sale || b.has_studs_available) && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`breeder-badges-${b.id}`}>
                    {b.has_dogs_for_sale && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#EEF8F1] text-[#0F7B3E] border border-[#BFE8C9] rounded-full px-2 py-0.5"
                        data-testid={`breeder-badge-dogs-${b.id}`}
                      >
                        <Storefront size={9} weight="fill" /> Dogs available
                      </span>
                    )}
                    {b.has_studs_available && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FFF0F0] text-[#7A1A1A] border border-[#F4C8C8] rounded-full px-2 py-0.5"
                        data-testid={`breeder-badge-studs-${b.id}`}
                      >
                        <Heart size={9} weight="fill" /> Studs available
                      </span>
                    )}
                  </div>
                )}
                {!isVerified && (
                  <div className="mt-3" onClick={(e) => e.preventDefault()}>
                    <VerificationProgressBadge breederId={b.id} compact />
                  </div>
                )}
              </Link>
            </div>
          );})}
        </div>
      )}
      <KennelVerificationDrawer
        open={verifyDrawer.open}
        onOpenChange={(o) => setVerifyDrawer((s) => ({ ...s, open: o }))}
        breederId={verifyDrawer.id}
        breederName={verifyDrawer.name}
        onVerifiedChanged={onVerifiedChanged}
      />
    </AppShell>
  );
}
